import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, Chip, CircularProgress, Alert, Stack, Tabs, Tab
} from '@mui/material';
import axios from 'axios';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const VERIFICATION_STATUSES = ['unverified', 'pending', 'verified', 'rejected'];

const approvalColor = (status) => {
    if (status === 'approved') return 'success';
    if (status === 'rejected') return 'error';
    return 'warning';
};

const verificationColor = (status) => {
    if (status === 'verified') return 'success';
    if (status === 'rejected') return 'error';
    if (status === 'pending') return 'warning';
    return 'default';
};

const CarrierApproval = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(null); // tracks which row is processing
    const [currentTab, setCurrentTab] = useState(0);
    const roles = ['carrier', 'shipper', 'broker'];

    const getToken = () => localStorage.getItem('jwtToken');

    const fetchUsers = async (role) => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API_BASE_URL}/api/admin/account-approvals`, {
                headers: { Authorization: `Bearer ${getToken()}` },
                params: { role }
            });
            setUsers(data);
        } catch (err) {
            setError(`Failed to fetch ${role}s.`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchUsers(roles[currentTab]); }, [currentTab]);

    const handleApprovalAction = async (id, status) => {
        setActionLoading(`approval-${id}`);
        try {
            await axios.patch(
                `${API_BASE_URL}/api/admin/accounts/${id}/approval`,
                { status, role: roles[currentTab] },
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            await fetchUsers(roles[currentTab]);
        } catch {
            alert('Failed to update approval status.');
        } finally {
            setActionLoading(null);
        }
    };

    const handleVerificationAction = async (id, verificationStatus) => {
        setActionLoading(`verify-${id}`);
        try {
            const role = roles[currentTab];
            await axios.patch(
                `${API_BASE_URL}/api/admin/users/${role}/${id}/verification`,
                { admin_verification_status: verificationStatus, admin_verification_notes: '' },
                { headers: { Authorization: `Bearer ${getToken()}` } }
            );
            await fetchUsers(role);
        } catch {
            alert('Failed to update verification status.');
        } finally {
            setActionLoading(null);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ pt: 0, px: 0, pb: 3 }}>
            <Paper sx={{ mb: 2 }}>
                <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} variant="fullWidth">
                    <Tab label="Carriers" />
                    <Tab label="Shippers" />
                    <Tab label="Brokers" />
                </Tabs>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : users.length === 0 ? (
                <Alert severity="info">No {roles[currentTab]}s found.</Alert>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                    <Table size="small">
                        <TableHead sx={{ bgcolor: '#11224d' }}>
                            <TableRow>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.78rem' }}>Company Name</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.78rem' }}>Email</TableCell>
                                {roles[currentTab] !== 'shipper' && (
                                    <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.78rem' }}>FMCSA Email</TableCell>
                                )}
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.78rem' }}>Phone</TableCell>
                                {roles[currentTab] !== 'shipper' && (
                                    <>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.78rem' }}>DOT Number</TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.78rem' }}>MC Number</TableCell>
                                    </>
                                )}
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.78rem' }}>Verification Status</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.78rem', textAlign: 'center' }}>Verify Actions</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.78rem' }}>Approval Status</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.78rem', textAlign: 'center' }}>Approval Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((user) => (
                                <TableRow
                                    key={user.id}
                                    sx={{ '&:hover': { bgcolor: '#f9fafb' } }}
                                >
                                    <TableCell sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                                        {user.company_name}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem' }}>{user.email}</TableCell>
                                    {roles[currentTab] !== 'shipper' && (
                                        <TableCell sx={{ fontSize: '0.8rem' }}>
                                            {(user.role === 'carrier' || user.role === 'broker') ? (user.fmcsa_email || '—') : '—'}
                                        </TableCell>
                                    )}
                                    <TableCell sx={{ fontSize: '0.8rem' }}>{user.phone_number || '—'}</TableCell>
                                    {roles[currentTab] !== 'shipper' && (
                                        <>
                                            <TableCell sx={{ fontSize: '0.8rem' }}>
                                                {user.dot_number || '—'}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '0.8rem' }}>
                                                {user.mc_number && user.mc_number !== 'MC-' ? user.mc_number : '—'}
                                            </TableCell>
                                        </>
                                    )}
                                    {/* Verification Status */}
                                    <TableCell>
                                        <Chip label={user.admin_verification_status || 'unverified'} size="small" color={verificationColor(user.admin_verification_status)} sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                                    </TableCell>

                                    {/* Verification Actions */}
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <Stack direction="row" spacing={0.5} justifyContent="center">
                                            {user.admin_verification_status !== 'verified' && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="success"
                                                    disabled={actionLoading === `verify-${user.id}`}
                                                    onClick={() => handleVerificationAction(user.id, 'verified')}
                                                    sx={{ fontSize: '0.7rem', py: 0.3 }}
                                                >
                                                    Verify
                                                </Button>
                                            )}
                                            {user.admin_verification_status === 'verified' && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="warning"
                                                    disabled={actionLoading === `verify-${user.id}`}
                                                    onClick={() => handleVerificationAction(user.id, 'unverified')}
                                                    sx={{ fontSize: '0.7rem', py: 0.3 }}
                                                >
                                                    Unverify
                                                </Button>
                                            )}
                                            {user.admin_verification_status !== 'rejected' && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    disabled={actionLoading === `verify-${user.id}`}
                                                    onClick={() => handleVerificationAction(user.id, 'rejected')}
                                                    sx={{ fontSize: '0.7rem', py: 0.3 }}
                                                >
                                                    Reject
                                                </Button>
                                            )}
                                        </Stack>
                                    </TableCell>

                                    {/* Approval Status */}
                                    <TableCell>
                                        <Chip label={user.admin_approval_status || 'pending'} size="small" variant="outlined" color={approvalColor(user.admin_approval_status)} sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                                    </TableCell>

                                    {/* Approval Actions */}
                                    <TableCell sx={{ textAlign: 'center' }}>
                                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                            {user.admin_approval_status === 'approved' && (
                                                <Chip
                                                    icon={<CheckCircleIcon sx={{ fontSize: '1rem !important' }} />}
                                                    label="Approved"
                                                    color="success"
                                                    size="small"
                                                    sx={{ fontSize: '0.7rem', fontWeight: 700 }}
                                                />
                                            )}
                                            {user.admin_approval_status !== 'approved' && (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="success"
                                                    disabled={actionLoading === `approval-${user.id}`}
                                                    onClick={() => handleApprovalAction(user.id, 'approved')}
                                                    sx={{ fontSize: '0.7rem', py: 0.3 }}
                                                >
                                                    Approve
                                                </Button>
                                            )}
                                            {user.admin_approval_status !== 'pending' && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="warning"
                                                    disabled={actionLoading === `approval-${user.id}`}
                                                    onClick={() => handleApprovalAction(user.id, 'pending')}
                                                    sx={{ fontSize: '0.7rem', py: 0.3 }}
                                                >
                                                    Unapprove
                                                </Button>
                                            )}
                                            {user.admin_approval_status !== 'rejected' && (
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    color="error"
                                                    disabled={actionLoading === `approval-${user.id}`}
                                                    onClick={() => handleApprovalAction(user.id, 'rejected')}
                                                    sx={{ fontSize: '0.7rem', py: 0.3 }}
                                                >
                                                    Reject
                                                </Button>
                                            )}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default CarrierApproval;